import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  getIntegrations,
  getAvailableIntegrations,
  getNewIntegrationsFields,
  createIntegrations,
  createOauthIntegrations,
  deleteIntegration,
  getIntegration,
  getIntegrationTools,
  updateIntegrations,
  getPostConfigurationFields,
  getCurveDentalOperatories,
  startInitialSync,
  manualSync,
  getSynchronizerLogs,
  getSynchronizedItems
} from './api';
import {
  AvailableIntegrationResponse,
  IntegrationResponse,
  InitialSyncResponse,
  SynchronizerLogsResponse,
  SynchronizedItemsResponse
} from './types';
import { useParams } from 'react-router-dom';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { queryClient } from 'providers/AllProviders';

export function useGetAvailableIntegrations(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: AvailableIntegrationResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: AvailableIntegrationResponse[]; meta: MetaApiResponse }>({
    queryKey: ['availableIntegration', params],
    queryFn: () => getAvailableIntegrations(params),
    ...options
  });
}

export function useGetNewIntegrationsFields(code: string, options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['newIntegrationField', code],
    queryFn: () => getNewIntegrationsFields(code),
    enabled: Boolean(code && !['google_calendar', 'cal_com'].includes(code)),
    ...options
  });
}

export function useGetPostConfigurationFields(
  id?: string,
  isPostConfigRequired?: boolean,
  options?: UseQueryOptions<any, Error>
) {
  return useQuery<any>({
    queryKey: ['post_configs', id, isPostConfigRequired],
    queryFn: () => getPostConfigurationFields(id),
    enabled: Boolean(id && isPostConfigRequired),
    ...options
  });
}

export function useGetIntegrations(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: IntegrationResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: IntegrationResponse[]; meta: MetaApiResponse }>({
    queryKey: ['integrations', params],
    queryFn: () => getIntegrations(params),
    ...options
  });
}

export function useCreateIntegration(options?: UseMutationOptions<any, Error, any>) {
  return useMutation({
    mutationFn: (payload: any) => createIntegrations(payload),
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations'],
        refetchType: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['integration']
      });
      queryClient.refetchQueries({
        queryKey: ['billing_history']
      });
    }
  });
}

export function useUpdateIntegration(options?: UseMutationOptions<any, Error, any>) {
  const { integrationId } = useParams();

  return useMutation({
    mutationFn: (payload: any) => updateIntegrations(integrationId!, payload),
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations'],
        refetchType: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['integration']
      });
    }
  });
}

export function useCreateOauthIntegration(options?: UseMutationOptions<any, Error, any>) {
  const payload = {
    oauth_state: {
      redirect_uri: window.location.href.replace(/\/add$/, '')
    }
  };
  return useMutation({
    mutationFn: (data: any) => {
      const { code, configuration, integration_id, id, ...oauthState } = data;

      return createOauthIntegrations(code, {
        ...payload,
        oauth_state: {
          ...payload.oauth_state,
          ...oauthState,
          integration_id: integration_id ?? id,
          configuration: configuration ?? {}
        }
      });
    },
    ...options,
    onSuccess: (data: any) => {
      if (data?.url) {
        window.location.href = data.url;
      }
      queryClient.invalidateQueries({
        queryKey: ['availableIntegration'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['integration'],
        refetchType: 'active'
      });
    }
  });
}

export function useDeleteIntegration() {
  return useMutation({
    mutationFn: (id: number) => deleteIntegration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['availableIntegration'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['integration'],
        refetchType: 'active'
      });
    }
  });
}

export function useGetIntegration(
  id?: number,
  params?: Record<string, any>,
  options?: UseQueryOptions<IntegrationResponse, Error>
) {
  return useQuery<any>({
    queryKey: ['integration', id, params],
    queryFn: () => getIntegration(id!, params),
    enabled: Boolean(id),
    ...options
  });
}

export function useGetIntegrationTools(integrationId?: string, options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['integrationTools', integrationId],
    queryFn: () => getIntegrationTools(integrationId!),
    enabled: Boolean(integrationId),
    ...options
  });
}

export function useGetCurveDentalOperatories(
  integrationId?: string,
  providerId?: number,
  options?: UseQueryOptions<any, Error>
) {
  return useQuery<any>({
    queryKey: ['curve_dental_operatories', integrationId, providerId],
    queryFn: () => getCurveDentalOperatories(integrationId, providerId),
    enabled: Boolean(integrationId && providerId),
    ...options
  });
}

export function useStartInitialSync(options?: UseMutationOptions<InitialSyncResponse, Error, void>) {
  const { integrationId } = useParams();

  return useMutation({
    mutationFn: () => startInitialSync(integrationId!),
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['integration'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['synchronizerLogs', integrationId],
        refetchType: 'active'
      });
    }
  });
}

export function useManualSync(options?: UseMutationOptions<InitialSyncResponse, Error, void>) {
  const { integrationId } = useParams();

  return useMutation({
    mutationFn: () => manualSync(integrationId!),
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['integration'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['synchronizerLogs', integrationId],
        refetchType: 'active'
      });
    }
  });
}

export function useGetSynchronizerLogs(
  params?: Record<string, any>,
  options?: UseQueryOptions<SynchronizerLogsResponse, Error>
) {
  return useQuery<SynchronizerLogsResponse, Error>({
    queryKey: ['synchronizerLogs', params],
    queryFn: () => getSynchronizerLogs(params),
    enabled: Boolean(params?.integration_id),
    ...options
  });
}

export function useGetSynchronizedItems(
  synchronizerLogId?: number | null,
  page?: number,
  options?: UseQueryOptions<SynchronizedItemsResponse, Error>
) {
  return useQuery<SynchronizedItemsResponse, Error>({
    queryKey: ['synchronizedItems', synchronizerLogId, page],
    queryFn: () => getSynchronizedItems(synchronizerLogId!, { page }),
    enabled: Boolean(synchronizerLogId),
    ...options
  });
}
